"use client";

import { ListCard } from "@/components/shared/list-card";
import { track } from "@/lib/analytics/data-layer";
import { toCardDateAndPlace } from "@/lib/gift-list-search/civil-date";
import type { PublicGiftListItem } from "@/lib/gift-list-search/public-search-types";
import { publicListHref } from "@/lib/public-list/public-list-href";

const LIST_KIND = "Casamento";

interface PublicGiftListCardProps {
  item: PublicGiftListItem;
  /** The result's place in the grid, from 1; with it, pressing the card is reported (`list_card_selected`). */
  position?: number;
}

/** The words and the photograph a search result wears on the one list card, with no target. */
export type SearchResultCardContent = {
  readonly eyebrow: string;
  readonly title: string;
  readonly supportLine: string;
  readonly coverSrc: string | null;
};

/**
 * Canon §13 and Busca de Listas: the eyebrow is the kind of event, the title is the names (the
 * list's title only when both are missing), the support line is date and place. Shared with the
 * backoffice's internal search, so both surfaces draw a result the same way.
 */
export function toSearchResultCardContent(item: PublicGiftListItem): SearchResultCardContent {
  const names = [item.personName1, item.personName2].filter(
    (name): name is string => name !== null,
  );

  return {
    // The public search does not send the list's type; every list the product creates is a
    // wedding (the MVP's only kind), the one the reference writes here.
    eyebrow: LIST_KIND,
    title: names.length > 0 ? names.join(" & ") : item.title,
    supportLine: toCardDateAndPlace(item.eventDate, item.eventLocation),
    coverSrc: item.featuredImageUrl,
  };
}

/**
 * A search result as the one list card (canon §13). By id: the public search returns no slug,
 * and the page redirects to the slug once it knows it.
 */
export function PublicGiftListCard({ item, position }: PublicGiftListCardProps) {
  return (
    <ListCard
      {...toSearchResultCardContent(item)}
      href={publicListHref(item.id)}
      onNavigate={
        position === undefined
          ? undefined
          : () => track({ event: "list_card_selected", list_id: item.id, card_context: "search", position })
      }
    />
  );
}
